import React, { useRef, useState } from 'react';

// ─── Custom Audio Player ────────────────────────────────────────────────────
export function AudioPlayer({ src, isOutbound }: { src: string; isOutbound: boolean }) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [speed, setSpeed] = useState(1);

    const formatTime = (s: number) => {
        if (!isFinite(s) || isNaN(s)) return '0:00';
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, '0')}`;
    };

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
    };

    const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!audioRef.current || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        audioRef.current.currentTime = ratio * duration;
    };

    const cycleSpeed = () => {
        const speeds = [1, 1.5, 2];
        const next = speeds[(speeds.indexOf(speed) + 1) % speeds.length];
        setSpeed(next);
        if (audioRef.current) audioRef.current.playbackRate = next;
    };

    // Fake waveform bars (static decorative)
    const BARS = [3,5,8,6,10,7,12,9,7,5,8,11,6,9,4,7,10,8,5,9,12,7,6,10,8,5,7,9,6,8];

    const outCls = 'bg-green-700/30 text-white';
    const inCls  = 'bg-slate-100 dark:bg-slate-700/60 text-slate-800 dark:text-slate-100';
    const barActive   = isOutbound ? 'bg-white' : 'bg-green-500 dark:bg-green-400';
    const barInactive = isOutbound ? 'bg-white/35' : 'bg-slate-300 dark:bg-slate-500';
    const progressBg  = isOutbound ? 'bg-white/25' : 'bg-slate-200 dark:bg-slate-600';
    const progressFill = isOutbound ? 'bg-white' : 'bg-green-500 dark:bg-green-400';
    const btnCls = isOutbound
        ? 'bg-white/25 hover:bg-white/40 text-white border border-white/20'
        : 'bg-green-700 hover:bg-green-800 text-white';
    const timeCls = isOutbound ? 'text-white/80' : 'text-slate-500 dark:text-slate-400';
    const speedCls = isOutbound
        ? 'text-white/80 hover:text-white'
        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200';

    return (
        <div className={`my-1 -mx-1 rounded-2xl px-3 py-2.5 flex flex-col gap-2 min-w-[220px] max-w-[260px] ${isOutbound ? outCls : inCls}`}>
            <audio
                ref={audioRef}
                src={src}
                preload="none"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => { setIsPlaying(false); setProgress(0); setCurrentTime(0); }}
                onTimeUpdate={() => {
                    if (!audioRef.current) return;
                    setCurrentTime(audioRef.current.currentTime);
                    setProgress(audioRef.current.duration ? (audioRef.current.currentTime / audioRef.current.duration) * 100 : 0);
                }}
                onLoadedMetadata={() => {
                    if (audioRef.current) setDuration(audioRef.current.duration);
                }}
            />

            {/* Row 1: play + waveform */}
            <div className="flex items-center gap-2.5">
                {/* Play/Pause */}
                <button
                    type="button"
                    onClick={togglePlay}
                    className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all shadow-sm active:scale-95 ${btnCls}`}
                >
                    {isPlaying ? (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                            <rect x="1" y="1" width="4" height="12" rx="1.5"/>
                            <rect x="9" y="1" width="4" height="12" rx="1.5"/>
                        </svg>
                    ) : (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                            <path d="M3 2.5l9 4.5-9 4.5V2.5z"/>
                        </svg>
                    )}
                </button>

                {/* Waveform + Progress */}
                <div className="flex-1 flex flex-col gap-1.5">
                    {/* Waveform bars */}
                    <div
                        className="flex items-end gap-[2px] h-7 cursor-pointer"
                        onClick={handleSeek}
                    >
                        {BARS.map((h, i) => {
                            const pct = (i / BARS.length) * 100;
                            const filled = pct <= progress;
                            return (
                                <div
                                    key={i}
                                    className={`flex-1 rounded-full transition-colors duration-75 ${filled ? barActive : barInactive}`}
                                    style={{ height: `${Math.max(25, (h / 12) * 100)}%` }}
                                />
                            );
                        })}
                    </div>
                    {/* Progress bar (thin) */}
                    <div
                        className={`h-0.5 w-full rounded-full cursor-pointer ${progressBg}`}
                        onClick={handleSeek}
                    >
                        <div
                            className={`h-full rounded-full transition-all ${progressFill}`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Row 2: time + speed */}
            <div className="flex items-center justify-between px-0.5">
                <span className={`text-[11px] font-mono font-medium ${timeCls}`}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                </span>
                <button
                    type="button"
                    onClick={cycleSpeed}
                    className={`text-[11px] font-bold px-1.5 py-0.5 rounded transition-colors ${speedCls}`}
                    title="Velocidade"
                >
                    {speed}x
                </button>
            </div>
        </div>
    );
}
